import { Description } from '@mui/icons-material';
import { Badge, Chip } from '@mui/material';

import { formatDateTime } from '../../../utils/formatting';
import { ExplorableId } from '../../explorable-id';
import { bool, str } from '../field-access';
import type { ResourceDetailSlotProps } from '../types';
import { statusTone } from './status-tone';

/**
 * Note identity band, read from the registry record.
 *
 * The note document stores its creation time as `time_uploaded` (a Unix
 * timestamp) rather than an ISO `created_at`, so both are accepted.
 */
export function NoteHeader({ data, resourceId, scope }: ResourceDetailSlotProps) {
  const status = str(data, 'status');
  const isSigned = bool(data, 'is_signed');
  const title = str(data, 'internal_title') ?? str(data, 'note_title');

  const createdAt = str(data, 'created_at');
  const uploadedAt = data.time_uploaded;
  const created =
    createdAt ??
    (typeof uploadedAt === 'number'
      ? new Date(uploadedAt * 1000).toISOString()
      : undefined);

  return (
    <div className="bg-white rounded-lg shadow p-6 mb-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center">
            <Description className="mr-2 h-6 w-6" />
            {title || 'Note'}
          </h1>
          <p className="text-sm text-gray-600 mt-1">
            {created ? `Created ${formatDateTime(created)}` : '—'}
          </p>
          <div className="mt-2 flex flex-wrap items-center gap-1.5">
            <ExplorableId
              label="note"
              value={resourceId}
              organizationId={scope.organizationId}
            />
          </div>
        </div>
        <div className="flex items-center gap-3 shrink-0">
          {isSigned && <Chip label="Signed" color="success" size="small" />}
          {status && (
            <Badge color={statusTone(status)}>{status.replace('_', ' ')}</Badge>
          )}
        </div>
      </div>
    </div>
  );
}
